import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { SpaBadge } from '@/components/ui/spa-badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Loader2, MapPin, ArrowLeft, Clock, CheckCircle, XCircle, Eye, ArrowRight,
  CalendarDays, Gift, ExternalLink, Video,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from '@/hooks/use-toast';

interface Interview {
  id: string;
  scheduled_at: string;
  duration_minutes: number;
  location: string | null;
  meeting_link: string | null;
  notes: string | null;
  status: string;
}

interface Offer {
  id: string;
  message: string;
  salary_range: string | null;
  start_date: string | null;
  conditions: string | null;
  status: string;
  created_at: string;
}

interface Application {
  id: string;
  status: string;
  cover_message: string | null;
  company_message: string | null;
  created_at: string;
  updated_at: string;
  opportunity: {
    id: string; title: string; type: string; sector: string;
    country: string; city: string | null; spa_linked: boolean;
    deadline: string | null; status: string;
    company: { name: string; };
  };
}

const statusConfig: Record<string, { icon: typeof Clock; color: string; key: string }> = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', key: 'applications.status.pending' },
  reviewed: { icon: Eye, color: 'bg-blue-100 text-blue-800', key: 'applications.status.reviewed' },
  shortlisted: { icon: CheckCircle, color: 'bg-primary/10 text-primary', key: 'applications.status.shortlisted' },
  interview_scheduled: { icon: CalendarDays, color: 'bg-purple-100 text-purple-800', key: 'Interview Scheduled' },
  accepted: { icon: Gift, color: 'bg-success/10 text-success', key: 'Offer Received' },
  rejected: { icon: XCircle, color: 'bg-destructive/10 text-destructive', key: 'applications.status.rejected' },
  withdrawn: { icon: XCircle, color: 'bg-muted text-muted-foreground', key: 'applications.status.withdrawn' },
};

export default function MyApplications() {
  const { t } = useTranslation();
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [professionalId, setProfessionalId] = useState<string | null>(null);

  // Interview/offer detail state
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [selectedOffer, setSelectedOffer] = useState<{ offer: Offer; appId: string } | null>(null);
  const [respondingToOffer, setRespondingToOffer] = useState(false);

  // Interviews and offers maps
  const [interviews, setInterviews] = useState<Record<string, Interview>>({});
  const [offers, setOffers] = useState<Record<string, Offer>>({});

  useEffect(() => {
    if (!authLoading && (!user || role !== 'professional')) { navigate('/auth'); return; }
    if (user) fetchApplications();
  }, [user, role, authLoading]);

  const fetchApplications = async () => {
    if (!user) return;
    try {
      const { data: prof } = await supabase.from('professionals').select('id').eq('user_id', user.id).single();
      if (!prof) { setLoading(false); return; }
      setProfessionalId(prof.id);

      const { data, error } = await supabase
        .from('applications')
        .select('id, status, cover_message, company_message, created_at, updated_at, opportunity:opportunities(id, title, type, sector, country, city, spa_linked, deadline, status, company:companies(name))')
        .eq('professional_id', prof.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const apps = (data as unknown as Application[]) || [];
      setApplications(apps);

      // Fetch interviews for interview_scheduled apps
      const interviewAppIds = apps.filter(a => a.status === 'interview_scheduled').map(a => a.id);
      if (interviewAppIds.length > 0) {
        const { data: intData } = await supabase
          .from('interviews')
          .select('*')
          .in('application_id', interviewAppIds)
          .order('scheduled_at', { ascending: true });

        if (intData) {
          const map: Record<string, Interview> = {};
          intData.forEach((i: Interview & { application_id: string }) => { map[i.application_id] = i as Interview; });
          setInterviews(map);
        }
      }

      // Fetch offers for accepted apps
      const offerAppIds = apps.filter(a => a.status === 'accepted').map(a => a.id);
      if (offerAppIds.length > 0) {
        const { data: offData } = await supabase
          .from('offers')
          .select('*')
          .in('application_id', offerAppIds)
          .order('created_at', { ascending: false });

        if (offData) {
          const map: Record<string, Offer> = {};
          offData.forEach((o: Offer & { application_id: string }) => { map[o.application_id] = o as Offer; });
          setOffers(map);
        }
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally { setLoading(false); }
  };

  const handleWithdraw = async (applicationId: string) => {
    try {
      const { error } = await supabase.from('applications').update({ status: 'withdrawn' as const }).eq('id', applicationId);
      if (error) throw error;
      setApplications(prev => prev.map(a => a.id === applicationId ? { ...a, status: 'withdrawn' } : a));
      toast({ title: 'Application withdrawn' });
    } catch (error) {
      console.error('Error withdrawing application:', error);
    }
  };

  const respondToOffer = async (offerId: string, appId: string, response: 'accepted' | 'declined') => {
    setRespondingToOffer(true);
    try {
      const { error } = await supabase.from('offers').update({
        status: response,
        responded_at: new Date().toISOString(),
      }).eq('id', offerId);

      if (error) throw error;

      setOffers(prev => ({ ...prev, [appId]: { ...prev[appId], status: response } }));
      setSelectedOffer(null);
      toast({ title: response === 'accepted' ? 'Offer Accepted! 🎉' : 'Offer Declined', description: response === 'accepted' ? 'Congratulations on your new position!' : 'The company has been notified.' });
    } catch (error) {
      console.error('Error responding to offer:', error);
      toast({ title: 'Error', description: 'Failed to respond to offer', variant: 'destructive' });
    } finally { setRespondingToOffer(false); }
  };

  if (authLoading || loading) {
    return <Layout><div className="container flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>;
  }

  const getStatusLabel = (status: string) => {
    const config = statusConfig[status];
    if (!config) return status;
    // For translatable keys, try translation; fallback to key itself
    const translated = t(config.key);
    return translated === config.key ? config.key : translated;
  };

  return (
    <Layout>
      <div className="container py-8">
        <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate('/dashboard/professional')}>
          <ArrowLeft className="h-4 w-4" /> {t('common.back')}
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold">{t('applications.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('applications.description')}</p>
        </div>

        {applications.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{t('applications.noApplications')}</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/opportunities')}>
              {t('professional.dashboard.browseOpportunities')}
            </Button>
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {applications.map(app => {
              const config = statusConfig[app.status] || statusConfig.pending;
              const StatusIcon = config.icon;
              const interview = interviews[app.id];
              const offer = offers[app.id];

              return (
                <Card key={app.id} className={`transition-shadow hover:shadow-md ${app.status === 'accepted' ? 'border-success/30 bg-success/5' : ''} ${app.status === 'interview_scheduled' ? 'border-purple-200 bg-purple-50/50' : ''}`}>
                  <CardContent className="p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className={config.color}>
                            <StatusIcon className="mr-1 h-3 w-3" />
                            {getStatusLabel(app.status)}
                          </Badge>
                          <TypeBadge type={app.opportunity.type as any} />
                          <SectorBadge sector={app.opportunity.sector as any} />
                          {app.opportunity.spa_linked && <SpaBadge size="sm" />}
                        </div>
                        <Link to={`/opportunities/${app.opportunity.id}`} className="text-lg font-semibold hover:text-primary">
                          {app.opportunity.title}
                        </Link>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <span>{app.opportunity.company?.name}</span>
                          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{app.opportunity.city ? `${app.opportunity.city}, ` : ''}{app.opportunity.country}</span>
                          <span>{t('applications.appliedOn')} {format(new Date(app.created_at), 'MMM d, yyyy')}</span>
                        </div>

                        {/* Company message */}
                        {app.company_message && (
                          <div className="mt-2 p-3 bg-muted rounded-lg text-sm">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Message from employer:</p>
                            <p>{app.company_message}</p>
                          </div>
                        )}

                        {/* Interview details */}
                        {app.status === 'interview_scheduled' && interview && (
                          <div className="mt-2 p-3 border border-accent bg-accent/30 rounded-lg text-sm space-y-1">
                            <p className="font-semibold flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-accent-foreground" /> Interview Details</p>
                            <p><strong>Date:</strong> {format(new Date(interview.scheduled_at), 'PPP \'at\' h:mm a')}</p>
                            <p><strong>Duration:</strong> {interview.duration_minutes} minutes</p>
                            {interview.location && <p><strong>Location:</strong> {interview.location}</p>}
                            {interview.meeting_link && (
                              <p className="flex items-center gap-1.5">
                                <Video className="h-3.5 w-3.5" />
                                <a href={interview.meeting_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                  Join Meeting
                                </a>
                              </p>
                            )}
                            {interview.notes && <p className="text-muted-foreground mt-1">{interview.notes}</p>}
                          </div>
                        )}

                        {/* Offer details */}
                        {app.status === 'accepted' && offer && (
                          <div className="mt-2 p-3 border border-success/30 bg-success/5 rounded-lg text-sm space-y-2">
                            <p className="font-semibold flex items-center gap-1.5"><Gift className="h-4 w-4 text-success" /> Offer Details</p>
                            <p>{offer.message}</p>
                            {offer.salary_range && <p><strong>Compensation:</strong> {offer.salary_range}</p>}
                            {offer.start_date && <p><strong>Start Date:</strong> {format(new Date(offer.start_date), 'PPP')}</p>}
                            {offer.conditions && <p><strong>Conditions:</strong> {offer.conditions}</p>}

                            {offer.status === 'pending' && (
                              <div className="flex gap-2 pt-2">
                                <Button size="sm" className="bg-success hover:bg-success/90 text-success-foreground"
                                  onClick={() => setSelectedOffer({ offer, appId: app.id })}>
                                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Accept Offer
                                </Button>
                                <Button size="sm" variant="outline"
                                  onClick={() => respondToOffer(offer.id, app.id, 'declined')}>
                                  Decline
                                </Button>
                              </div>
                            )}
                            {offer.status === 'accepted' && (
                              <Badge className="bg-success/10 text-success"><CheckCircle className="mr-1 h-3 w-3" /> Offer Accepted</Badge>
                            )}
                            {offer.status === 'declined' && (
                              <Badge variant="secondary">Offer Declined</Badge>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex shrink-0 gap-2">
                        {app.status === 'pending' && (
                          <Button variant="outline" size="sm" onClick={() => handleWithdraw(app.id)}>
                            {t('applications.withdraw')}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/opportunities/${app.opportunity.id}`}><ArrowRight className="h-4 w-4" /></Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Accept Offer Confirmation */}
        <Dialog open={!!selectedOffer} onOpenChange={(open) => !open && setSelectedOffer(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-success" /> Accept This Offer?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to accept this offer? This action confirms your intent to proceed with the position.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedOffer(null)}>Cancel</Button>
              <Button className="bg-success hover:bg-success/90 text-success-foreground"
                onClick={() => selectedOffer && respondToOffer(selectedOffer.offer.id, selectedOffer.appId, 'accepted')}
                disabled={respondingToOffer}>
                {respondingToOffer ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Accept Offer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
